import React from 'react';
import { noop } from 'lodash';

import { CompareContext, CompareMetadata } from '@neptune/compare-domain';
import { useMemoizedValue } from '@neptune/shared/common-util';

import { isNotUndefined } from 'common/tsHelpers';

import { orderSelectedEntities } from './order-selected-entities';

type CompareContextType = React.ComponentProps<typeof CompareContext.Provider>['value'];
type CompareContextValueWithoutMutations = Omit<
  CompareContextType,
  'addToCompare' | 'hideFromCompare' | 'removeFromCompare' | 'clearCompare' | 'removeAllFromCompare'
>;

export type CompareEntitiesFilter = (
  runIds: string[],
  experimentNames: string[],
) => {
  metadata?: CompareMetadata;
  identificationKeys: {
    runIds: string[];
    experimentNames: string[];
  };
};

export type BuildCompareContextValueProps = {
  autoCompareLimit?: number;
  experimentNameToRunId?: Map<string, string>;
  showOnlySelectedRuns?: boolean;
  showSelectedHiddenByFilter?: boolean;
  filterEntities?: CompareEntitiesFilter;
  runIdsReady?: boolean;
  showOnlySelectedRunsChange: (value: boolean) => void;
  showSelectedHiddenByFilterChange: (value: boolean) => void;
  autoModeChange: (autoCompareLimit: number | undefined) => void;
  runIds?: string[];
  experimentNames?: string[];
  addedRunIds?: string[];
  addedExperimentNames?: string[];
  canEditCompare: boolean;
};

const EMPTY_SELECTION: string[] = [];
const EMPTY_MAP = new Map<string, string>();

function useSelection(
  runIds: string[],
  experimentNames: string[],
  experimentNameToRunId: Map<string, string>,
): string[] {
  return useMemoizedValue(
    React.useMemo(
      () =>
        Array.from(
          new Set([...runIds, ...experimentNames.map((name) => experimentNameToRunId.get(name))]),
        ).filter(Boolean) as string[],
      [runIds, experimentNames, experimentNameToRunId],
    ),
  );
}

function defaultFilterEntities(runIds: string[], experimentNames: string[]) {
  return { identificationKeys: { runIds, experimentNames }, metadata: new Map() };
}

export function useBuildCompareContextValue({
  autoCompareLimit,
  experimentNameToRunId = EMPTY_MAP,
  showOnlySelectedRuns,
  showSelectedHiddenByFilter,
  filterEntities = defaultFilterEntities,
  runIdsReady,
  showOnlySelectedRunsChange,
  showSelectedHiddenByFilterChange,
  autoModeChange,
  runIds = EMPTY_SELECTION,
  experimentNames = EMPTY_SELECTION,
  addedRunIds = runIds,
  addedExperimentNames = experimentNames,
  canEditCompare,
}: BuildCompareContextValueProps): CompareContextValueWithoutMutations {
  const {
    metadata,
    identificationKeys: { runIds: filteredRunIds, experimentNames: filteredExperimentNames },
  } = React.useMemo(
    () => filterEntities(runIds, experimentNames),
    [filterEntities, runIds, experimentNames],
  );

  const {
    identificationKeys: {
      runIds: filteredAddedRunIds,
      experimentNames: filteredAddedExperimentNames,
    },
  } = React.useMemo(
    () => filterEntities(addedRunIds, addedExperimentNames),
    [addedRunIds, addedExperimentNames, filterEntities],
  );

  const runsOrderMap = React.useMemo(() => {
    if (!metadata) {
      return new Map();
    }

    return orderSelectedEntities([{ metadata, index: 1 }]);
  }, [metadata]);

  const allSelectedIds = useSelection(runIds, experimentNames, experimentNameToRunId);
  const filteredSelectedIds = useSelection(
    filteredRunIds,
    filteredExperimentNames,
    experimentNameToRunId,
  );

  const runIdToExperimentName = React.useMemo(() => {
    const experimentNamesToMap = showSelectedHiddenByFilter
      ? experimentNames
      : filteredExperimentNames;

    return Object.fromEntries(
      Array.from(experimentNameToRunId.entries())
        .map(([experimentName, runId]) =>
          experimentNamesToMap.includes(experimentName) ? [runId, experimentName] : undefined,
        )
        .filter(isNotUndefined),
    );
  }, [experimentNameToRunId, experimentNames, filteredExperimentNames, showSelectedHiddenByFilter]);

  const isSelectedAsExperiment = React.useCallback(
    (experimentName?: string) => {
      if (!experimentName) {
        return false;
      }

      return filteredExperimentNames.includes(experimentName);
    },
    [filteredExperimentNames],
  );

  const isSelectedAsRun = React.useCallback(
    (runId?: string) => {
      if (!runId) {
        return false;
      }

      return filteredRunIds.includes(runId);
    },
    [filteredRunIds],
  );

  const isAdded = React.useCallback(
    (key?: string) => {
      if (!key) {
        return false;
      }

      return filteredAddedExperimentNames.includes(key) || filteredAddedRunIds.includes(key);
    },
    [filteredAddedExperimentNames, filteredAddedRunIds],
  );

  const knownIdsAndNames = useMemoizedValue([
    ...new Set([...addedRunIds, ...addedExperimentNames]),
  ]);
  const selectedKnownIdsAndNames = useMemoizedValue([...new Set([...runIds, ...experimentNames])]);
  const filteredKnownIdsAndNames = useMemoizedValue([
    ...new Set([...filteredRunIds, ...filteredExperimentNames]),
  ]);
  const filteredExperimentNamesCount = new Set(filteredExperimentNames).size;
  const filteredRunIdsCount = new Set(filteredRunIds).size;
  const filteredAddedExperimentNamesCount = new Set(filteredAddedExperimentNames).size;
  const filteredAddedRunIdsCount = new Set(filteredAddedRunIds).size;

  const selectedVisibleRunIds = showSelectedHiddenByFilter ? allSelectedIds : filteredSelectedIds;
  const selectedVisibleTotalCount = showSelectedHiddenByFilter
    ? selectedKnownIdsAndNames.length
    : filteredExperimentNamesCount + filteredRunIdsCount;

  return React.useMemo(() => {
    const selectedCount = selectedKnownIdsAndNames.length;
    const hiddenByFilterCount = selectedKnownIdsAndNames.length - selectedVisibleTotalCount;
    const count = selectedVisibleRunIds.length;

    return {
      count,
      runsOrderMap,
      hiddenCount: hiddenByFilterCount,
      selectedCount,
      filteredRunIdsCount,
      filteredExperimentNamesCount,
      filteredAddedRunIdsCount,
      filteredAddedExperimentNamesCount,
      runIds: selectedVisibleRunIds,
      autoCompareLimit,
      knownIdsAndNames,
      filteredKnownIdsAndNames,
      autoModeChange: canEditCompare ? autoModeChange : noop,
      canEditCompare,
      showOnlySelectedRuns,
      showOnlySelectedRunsChange,
      showSelectedHiddenByFilter,
      showSelectedHiddenByFilterChange,
      runIdsReady,
      runIdToExperimentName,
      isSelectedAsExperiment,
      isSelectedAsRun,
      isAdded,
      selectedExperimentNames: experimentNames,
      selectedRunIds: runIds,
      addedExperimentNames,
      addedRunIds,
    } satisfies CompareContextValueWithoutMutations;
  }, [
    selectedKnownIdsAndNames.length,
    selectedVisibleTotalCount,
    selectedVisibleRunIds,
    runsOrderMap,
    filteredRunIdsCount,
    filteredExperimentNamesCount,
    filteredAddedRunIdsCount,
    filteredAddedExperimentNamesCount,
    autoCompareLimit,
    knownIdsAndNames,
    filteredKnownIdsAndNames,
    canEditCompare,
    autoModeChange,
    showOnlySelectedRuns,
    showOnlySelectedRunsChange,
    showSelectedHiddenByFilter,
    showSelectedHiddenByFilterChange,
    runIdsReady,
    runIdToExperimentName,
    isSelectedAsExperiment,
    isSelectedAsRun,
    isAdded,
    experimentNames,
    runIds,
    addedExperimentNames,
    addedRunIds,
  ]);
}
